import { produce } from "immer";
import { uniqBy } from "lodash-es";
import { type Component, type FC, type ReactElement, useCallback } from "react";
import { type DndComponentClass } from "react-dnd";

import { type IMutantGenome } from "../../../../redux/modules/genome-browser.js";
import { Checkbox } from "../../../core/forms/Checkbox.js";
import { DragDropTrackMutantGenome, type IDragDropItemProps } from "../../../core/forms/DragDropItem.js";
import {
  GenericSortSelectionForm,
  type IGenericSortSelectionFormProps,
  type IGenericSortSelectionFormState,
} from "../../../core/forms/GenericSortSelectionForm.js";

interface MutantSortSelectionProps {
  mutantGenomes: IMutantGenome[];
  selectedMutantGenomes: IMutantGenome[];
  setSelectedMutantGenomes: (mutantGenomes: IMutantGenome[]) => void;
  setMutantGenomes: (mutantGenomes: IMutantGenome[]) => void;
}

const MutantSortSelection: FC<MutantSortSelectionProps> = (props) => {
  function renderMutantGenomeItem(index: number, item: IMutantGenome): ReactElement<IDragDropItemProps> {
    // we create dynamically a component
    const MutantGenomeItem: DndComponentClass<any, IDragDropItemProps> = DragDropTrackMutantGenome;
    const filtered = props.selectedMutantGenomes.find((genome) => genome.id === item.id);
    return (
      <MutantGenomeItem
        key={item.name}
        dataKey={item.name}
        dataIndex={index}
        onMoveColumn={moveMutantGenomeColumn}
        canDrag={() => true}
        canDrop={() => true}
      >
        <Checkbox
          id={"mutant_genome_" + item.name}
          label={<span>{item.name}</span>}
          disabled={false}
          checked={filtered}
          onChange={(event) => onMutantGenomeCheckboxClick(event.target.checked, item)}
        />
      </MutantGenomeItem>
    );
  }

  const moveMutantGenomeColumn = useCallback(
    (dragIndex, hoverIndex) => {
      const dragColumn = props.mutantGenomes[dragIndex];
      const delta: Array<[number, number?] | [number, number, IMutantGenome]> = [
        [dragIndex, 1],
        [hoverIndex, 0, dragColumn],
      ];
      const mutantGenomes = produce(props.mutantGenomes, (draft) => {
        for (const d of delta) {
          // @ts-ignore
          draft.splice(...d);
        }
      });
      props.setMutantGenomes(mutantGenomes);
    },
    [props.mutantGenomes],
  );

  const onMutantGenomeCheckboxClick = useCallback(
    (checked: boolean, checkedMutantGenome: IMutantGenome) => {
      let selectedMutantGenomes = props.selectedMutantGenomes;
      if (checked) {
        selectedMutantGenomes = [...props.selectedMutantGenomes, checkedMutantGenome];
      } else {
        selectedMutantGenomes = props.selectedMutantGenomes.filter(
          (mutantGenome) => mutantGenome.id !== checkedMutantGenome.id,
        );
      }
      props.setSelectedMutantGenomes(selectedMutantGenomes);
    },
    [props.selectedMutantGenomes],
  );

  const handleMutantGenomesSelection = useCallback(
    (filteredMutantGenomes: IMutantGenome[], select: boolean) => {
      if (select) {
        const selectedMutantGenomes = uniqBy(
          props.selectedMutantGenomes.concat(filteredMutantGenomes),
          (genome) => genome.id,
        );
        props.setSelectedMutantGenomes(selectedMutantGenomes);
      } else {
        const selectedMutantGenomes = props.selectedMutantGenomes.filter(
          (selectedGenome) => !filteredMutantGenomes.find((fileredGenome) => fileredGenome.id === selectedGenome.id),
        );
        props.setSelectedMutantGenomes(selectedMutantGenomes);
      }
    },
    [props.selectedMutantGenomes],
  );
  type MutantGenomeReactCtor<P, S> = new (props: IGenericSortSelectionFormProps<IMutantGenome>) => Component<P, S>;

  const MutantSortSelectionForm: MutantGenomeReactCtor<
    IGenericSortSelectionFormProps<IMutantGenome>,
    IGenericSortSelectionFormState<IMutantGenome>
  > = GenericSortSelectionForm;
  const onFilterMutantGenomes = (filterTerm: string, mutantGenome: IMutantGenome): boolean =>
    mutantGenome.name.toLowerCase().includes(filterTerm?.toLowerCase());

  return (
    <MutantSortSelectionForm
      tooltip="Modify the genome selection"
      items={props.mutantGenomes}
      renderItem={renderMutantGenomeItem}
      onFilter={onFilterMutantGenomes}
      handleSelection={handleMutantGenomesSelection}
    />
  );
};

export default MutantSortSelection;
